import { useEffect, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { accountGroupQueries } from '#accounts/queries';
import { useLocalPref } from '#hooks/useLocalPref';

export function useAccountGroups() {
  return useQuery(accountGroupQueries.list());
}

export function useCollapsedAccountGroups(activeGroupIds?: string[]) {
  const [collapsed = [], setCollapsed] = useLocalPref(
    'ui.collapsedAccountGroups',
  );
  const activeIds = useMemo(
    () => (activeGroupIds ? new Set(activeGroupIds) : null),
    [activeGroupIds],
  );
  const cleaned = useMemo(
    () => (activeIds ? collapsed.filter(id => activeIds.has(id)) : collapsed),
    [activeIds, collapsed],
  );

  useEffect(() => {
    if (activeIds && cleaned.length !== collapsed.length) {
      setCollapsed(cleaned);
    }
  }, [activeIds, cleaned, collapsed, setCollapsed]);

  return {
    collapsedIds: cleaned,
    isCollapsed: (id: string) => cleaned.includes(id),
    toggle: (id: string) => {
      setCollapsed(
        cleaned.includes(id)
          ? cleaned.filter(groupId => groupId !== id)
          : [...cleaned, id],
      );
    },
  };
}

export type CollapsibleAccountSection = 'onbudget' | 'offbudget';

const collapsibleAccountSections = new Set<CollapsibleAccountSection>([
  'onbudget',
  'offbudget',
]);

function isCollapsibleAccountSection(
  value: string,
): value is CollapsibleAccountSection {
  return collapsibleAccountSections.has(value as CollapsibleAccountSection);
}

export function useCollapsedAccountSections() {
  const [stored = [], setStored] = useLocalPref('ui.collapsedAccountSections');
  const cleaned = useMemo(
    () => stored.filter(isCollapsibleAccountSection),
    [stored],
  );

  useEffect(() => {
    if (cleaned.length !== stored.length) {
      setStored(cleaned);
    }
  }, [cleaned, setStored, stored]);

  return {
    collapsedIds: cleaned,
    isCollapsed: (section: CollapsibleAccountSection) =>
      cleaned.includes(section),
    toggle: (section: CollapsibleAccountSection) => {
      setStored(
        cleaned.includes(section)
          ? cleaned.filter(value => value !== section)
          : [...cleaned, section],
      );
    },
  };
}
